import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Award, Heart, Briefcase, Zap, ChevronRight, GitCompareArrows } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const ShortlistedSalespeople = ({ token }) => {
  const navigate = useNavigate();
  const [shortlist, setShortlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    const fetchShortlist = async () => {
      try {
        const res = await fetch(`${API}/business/shortlist`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setShortlist(await res.json());
      } catch (error) {
        console.error('Failed to fetch shortlist');
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchShortlist();
  }, [token]);

  if (loading || shortlist.length === 0) return null;

  const badgeConfig = {
    GOLD: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
    SILVER: 'text-slate-300 bg-slate-300/10 border-slate-300/30',
    BRONZE: 'text-amber-600 bg-amber-600/10 border-amber-600/30'
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < 3) {
        next.add(id);
      }
      return next;
    });
  };

  const handleCompare = () => {
    const ids = Array.from(selected).join(',');
    navigate(`/compare?ids=${ids}`);
  };

  return (
    <div className="rounded-xl border border-white/10 bg-[#111317] p-5 mb-6" data-testid="shortlisted-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-red-400 fill-red-400" />
          <h3 className="font-semibold text-brand-text">My Shortlisted Salespeople</h3>
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-400/10 text-red-400">{shortlist.length}</span>
        </div>
        <div className="flex items-center gap-2">
          {selected.size >= 2 && (
            <button
              onClick={handleCompare}
              data-testid="compare-selected-btn"
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-brand-gold/10 text-brand-gold border border-brand-gold/30 hover:bg-brand-gold/20 transition-colors"
            >
              <GitCompareArrows className="w-3.5 h-3.5" />
              Compare ({selected.size})
            </button>
          )}
          {shortlist.length >= 2 && selected.size < 2 && (
            <span className="text-[10px] text-zinc-500">Select 2-3 to compare</span>
          )}
          <button
            onClick={() => navigate('/salespeople')}
            className="flex items-center gap-1 text-xs text-brand-gold hover:underline"
            data-testid="shortlist-view-all"
          >
            Browse Directory <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {shortlist.slice(0, 8).map((sp) => {
          const bc = sp.reputation?.badge ? badgeConfig[sp.reputation.badge] : null;
          const isSelected = selected.has(sp.id);
          return (
            <div
              key={sp.id}
              data-testid="shortlisted-card"
              className={`flex-shrink-0 w-44 rounded-lg border p-3 cursor-pointer transition-all duration-200 ${
                isSelected
                  ? 'border-brand-gold bg-brand-gold/5 ring-1 ring-brand-gold/40'
                  : 'border-white/5 bg-brand-bg hover:border-brand-gold/30'
              }`}
            >
              <div className="flex flex-col items-center text-center">
                {/* Select checkbox */}
                <div className="w-full flex justify-end mb-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleSelect(sp.id); }}
                    data-testid={`select-compare-${sp.id}`}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      isSelected
                        ? 'border-brand-gold bg-brand-gold text-black'
                        : 'border-zinc-600 hover:border-zinc-400'
                    }`}
                  >
                    {isSelected && <span className="text-[10px] font-bold">&#10003;</span>}
                  </button>
                </div>
                <div onClick={() => navigate(`/salespeople/${sp.id}`)}>
                  {sp.avatar ? (
                    <img src={sp.avatar} alt={sp.name} className="w-10 h-10 rounded-full object-cover border border-white/10 mb-2" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center text-brand-gold font-semibold mb-2">
                      {sp.name?.charAt(0)?.toUpperCase()}
                    </div>
                  )}
                  <div className="text-sm font-medium text-brand-text truncate w-full">{sp.name}</div>
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <Star className="w-3 h-3 text-brand-gold fill-brand-gold" />
                  <span className="text-xs text-brand-text">{sp.reputation?.avgStars?.toFixed(1) || '0.0'}</span>
                  {bc && (
                    <span className={`text-[9px] px-1 py-0 rounded border ${bc}`}>
                      <Award className="w-2 h-2 inline" />
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1 text-[10px] text-brand-muted">
                  {sp.completedDeals > 0 && (
                    <span className="flex items-center gap-0.5">
                      <Briefcase className="w-2.5 h-2.5" />{sp.completedDeals}
                    </span>
                  )}
                  {(sp.xp || 0) > 0 && (
                    <span className="flex items-center gap-0.5">
                      <Zap className="w-2.5 h-2.5" />{sp.xp}
                    </span>
                  )}
                </div>
                {sp.topAchievements?.[0] && (
                  <div className="mt-1.5" data-testid="shortlist-card-achievement">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-md border border-brand-gold/20 bg-brand-gold/5 text-brand-gold font-medium">
                      {sp.topAchievements[0].name}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ShortlistedSalespeople;
